import {
  NavModel,
  OffshoreEventType,
  OffshoreFormConfig,
  OffshoreVm,
} from '@navstation-portal/reporting/data-model/offshore-report';
import { OffshoreBunkerFormStore } from './form/bunker/offshore-bunker.store';
import { OffshoreConsumptionsFormStore } from './form/consumptions/offshore-consumptions.store';
import { OffshoreOperationalFormStore } from './form/operational/offshore-operational.store';
import { OffshorePositionAndWeatherFormStore } from './form/position-and-weather/offshore-position-and-weather.store';
import { OffshoreStockFormStore } from './form/stock/offshore-stock.store';
import { mockVesselSpecForm } from './mock-vessel-spec-form';
import { buildOffshoreNavModel } from './offshore-menu-vm-builders';
import { createDefaultTemplate, createTemplateFromFormConfig } from './offshore-nav-templates';

export interface OffshoreFormStores {
  operationalStore: InstanceType<typeof OffshoreOperationalFormStore>;
  positionAndWeatherStore: InstanceType<typeof OffshorePositionAndWeatherFormStore>;
  stockStore: InstanceType<typeof OffshoreStockFormStore>;
  consumptionsStore: InstanceType<typeof OffshoreConsumptionsFormStore>;
  bunkerStore: InstanceType<typeof OffshoreBunkerFormStore>;
}

export function buildOffshoreVm(stores: OffshoreFormStores): OffshoreVm {
  const vesselSpecForm: OffshoreFormConfig = mockVesselSpecForm;
  const reportConfig = stores.operationalStore.reportOptions();
  const isReportConfigComplete = !!reportConfig.eventType;
  const { navSections, validityMap } = createNavModel(vesselSpecForm, stores);
  const isFormValid = Object.values(validityMap).every((status) => status === 'valid');

  const isSectionVisible = (formConfig: OffshoreFormConfig, eventType: OffshoreEventType | undefined) => {
    return (tabId: string, sectionId?: string) => {
      if (!eventType) return false;
      const sections = formConfig.tabs?.[tabId]?.sections;
      if (!sections) return false;
      return sectionId
        ? (sections[sectionId]?.visible_types.includes(eventType) ?? false)
        : Object.values(sections).some((s) => s.visible_types.includes(eventType));
    };
  };

  const eventType = stores.operationalStore.reportOptions().eventType.value?.code as OffshoreEventType;
  return {
    isFieldVisible: isFieldVisible(vesselSpecForm, eventType),
    isFieldRequired: isFieldRequired(vesselSpecForm, eventType),
    isFieldReadOnly: isFieldReadOnly(vesselSpecForm, eventType),
    isSectionVisible: isSectionVisible(vesselSpecForm, eventType),
    isReportConfigComplete,
    navSections,
    validityMap,
    isFormValid,
  };
}

const isFieldVisible = (formConfig: OffshoreFormConfig, eventType: OffshoreEventType | undefined) => {
  return (tabId: string, sectionId: string, fieldId: string) => {
    const fieldConfig = formConfig.tabs?.[tabId]?.sections?.[sectionId]?.fields?.[fieldId];

    if (!fieldConfig) {
      console.warn(`No field config found for path: ${tabId}.${sectionId}.${fieldId}`);
      return false;
    }
    if (!eventType) return false;
    return fieldConfig.visible_types.includes(eventType);
  };
};

const isFieldRequired = (formConfig: OffshoreFormConfig, eventType: OffshoreEventType | undefined) => {
  return (tabId: string, sectionId: string, fieldId: string) => {
    if (!eventType) return false;
    const fieldConfig = formConfig.tabs?.[tabId]?.sections?.[sectionId]?.fields?.[fieldId];
    return fieldConfig?.required_types.includes(eventType) ?? false;
  };
};

const isFieldReadOnly = (formConfig: OffshoreFormConfig, eventType: OffshoreEventType | undefined) => {
  return (tabId: string, sectionId: string, fieldId: string) => {
    if (!eventType) return false;
    const fieldConfig = formConfig.tabs?.[tabId]?.sections?.[sectionId]?.fields?.[fieldId];
    return fieldConfig?.readonly_types.includes(eventType) ?? false;
  };
};

function createNavModel(vesselSpecForm: OffshoreFormConfig, stores: OffshoreFormStores): NavModel {
  const reportConfig = stores.operationalStore.reportOptions();
  const eventType = reportConfig.eventType.value?.code as OffshoreEventType;
  const defaultTemplate = createDefaultTemplate(() => undefined);

  if (!eventType) {
    return buildOffshoreNavModel({
      template: defaultTemplate,
      stores,
    });
  }

  const bunkerVm = stores.bunkerStore.vm();
  const stockVm = stores.stockStore.vm();
  const consumptionsVm = stores.consumptionsStore.vm();

  const customStatusResolvers = {
    bunkerTable: makeStatusResolver(bunkerVm, 'fuelOilBunkerTableStatus'),
    tankStockTable: makeStatusResolver(stockVm, 'isAllTanksStatus'),
    inUseTanks: makeStatusResolver(stockVm, 'inUseTanksStatus'),
    notInUseTanks: makeStatusResolver(stockVm, 'notInUseTanksStatus'),
    fuelROB: makeStatusResolver(stockVm, 'fuelRobTableStatus'),
    lcv: makeStatusResolver(consumptionsVm, 'lcvFieldsStatus'),
    sulphur: makeStatusResolver(consumptionsVm, 'sulphurFieldsStatus'),
    density: makeStatusResolver(consumptionsVm, 'densityFieldsStatus'),
    fuelTemp: makeStatusResolver(consumptionsVm, 'fuelTempFieldsStatus'),
    tankTransfers: () => {
      if (!stockVm?.hasStartedTankTransfers?.()) return 'hidden';
      return stockVm.tankTransfersStatus();
    },
    fuelSpecs: makeStatusResolver(consumptionsVm, 'fuelSpecsStatus'),
    totalConsumptions: makeStatusResolver(consumptionsVm, 'totalConsumptionsStatus'),
  };

  const reportTemplate = createTemplateFromFormConfig(vesselSpecForm, customStatusResolvers, eventType);
  const activeTemplate = [...defaultTemplate, ...reportTemplate];

  return buildOffshoreNavModel({
    template: activeTemplate,
    stores,
  });
}

function makeStatusResolver<T>(vm: T | undefined, method: keyof T, fallback = 'pending') {
  return () => (vm && typeof vm[method] === 'function' ? vm[method]() : fallback);
}
